import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { Language, LogService, Project } from 'htv-lib'
import languageList from '../../../../htv-lib/src/assets/data/language.json'
import { TranslateService } from '@ngx-translate/core'
import moment from 'moment'

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  isLoading$ = new BehaviorSubject<boolean>(true)

  readonly defaultLanguage: Language = { code: 'fr', countryCode: 'fr-FR', name: 'French', nativeName: 'Français' }
  readonly completeLanguageList: Language[] = languageList

  supportedLanguages = ['fr', 'en']
  availableLanguageList: Language[] = []

  selectedLanguage!: Language

  hasExplicitLanguageOverride = false

  constructor(
    private logService: LogService,
    private translateService: TranslateService
  ) {
    // do nothing
  }

  init(initialLanguageCode?: string): void {
    this.logService.info(`LanguageService - Initialization`)
    this.translateService.setDefaultLang(this.defaultLanguage.code)

    this.availableLanguageList = this.completeLanguageList.filter(lang => this.supportedLanguages.includes(lang.code))

    if (initialLanguageCode) {
      const language = this.getLanguageFromCode(initialLanguageCode)
      if (language) {
        this.hasExplicitLanguageOverride = true
        this.setSelectedLanguage(language)
      } else {
        this.setSelectedLanguage(this.defaultLanguage)
      }
    } else {
      this.setSelectedLanguage(this.defaultLanguage)
    }
  }

  initFromProject(project: Project): void {
    this.isLoading$.next(true)
    this.logService.info('LanguageService - Initializing with Humecloud data')

    const { generalConfig } = project.configuration
    this.supportedLanguages = generalConfig.supportedLanguages
    this.availableLanguageList = this.completeLanguageList.filter(lang => this.supportedLanguages.includes(lang.code))

    if (!this.hasExplicitLanguageOverride) {
      const configLanguage = this.getLanguageFromCode(generalConfig.defaultLanguage)
      this.setSelectedLanguage(configLanguage || this.defaultLanguage)
    }

    this.isLoading$.next(false)
  }

  getLanguageFromCode(code: string): Language | undefined {
    return this.completeLanguageList.find(lang => lang.code === code)
  }

  setSelectedLanguage(language: Language): void {
    this.logService.info(`[LanguageService] Selected language: ${language.code}`)
    this.selectedLanguage = language
    this.translateService.use(this.selectedLanguage.code)
    moment.locale(this.selectedLanguage.code)
  }

  setSelectedLanguageFromCode(code: string): void {
    const language = this.getLanguageFromCode(code)
    if (language) {
      this.hasExplicitLanguageOverride = true
      this.setSelectedLanguage(language)
    }
  }

  getSelectedLanguage(): Language {
    return this.selectedLanguage
  }
}
